import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Clock, ShieldCheck, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Canonical screen keys (must match backend SCREEN_KEYS in models.py + Layout.jsx keys)
const SCREEN_GROUPS = [
  {
    label: "Operacional",
    keys: [
      ["dashboard", "Dashboard"],
      ["courses", "Cursos"],
      ["my_track", "Minha trilha"],
      ["certificates", "Certificados"],
      ["recycling", "Reciclagem"],
      ["library", "Biblioteca"],
      ["ranking", "Ranking"],
    ],
  },
  {
    label: "Gestão",
    keys: [
      ["alerts", "Alertas de compliance"],
      ["matrix", "Matriz de capacitação"],
      ["admin_users", "Colaboradores"],
      ["admin_organization", "Estrutura"],
      ["admin_courses", "Gerenciar cursos"],
      ["admin_ai", "IA: gerar curso"],
      ["admin_settings", "Configurações"],
    ],
  },
  {
    label: "Master",
    keys: [
      ["admin_companies", "Empresas clientes"],
    ],
  },
];

export default function AdminOrg() {
  const [tab, setTab] = useState("branches");
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [f, setF] = useState({ branch: "", branch_loc: "", dept: "", dept_branch: "", pos: "", pos_dept: "" });

  const refresh = async () => {
    const [b, d, p, c] = await Promise.all([
      api.get("/branches"), api.get("/departments"), api.get("/positions"), api.get("/courses"),
    ]);
    setBranches(b.data); setDepartments(d.data); setPositions(p.data); setCourses(c.data);
  };
  useEffect(() => { refresh(); }, []);

  const addBranch = async () => { if (!f.branch) return; await api.post("/branches", { name: f.branch, location: f.branch_loc }); setF({ ...f, branch: "", branch_loc: "" }); toast.success("Filial criada"); refresh(); };
  const addDept = async () => { if (!f.dept) return; await api.post("/departments", { name: f.dept, branch_id: f.dept_branch || null }); setF({ ...f, dept: "", dept_branch: "" }); toast.success("Departamento criado"); refresh(); };
  const addPos = async () => { if (!f.pos) return; await api.post("/positions", { name: f.pos, department_id: f.pos_dept || null, required_course_ids: [], track: [], screen_permissions: null, can_edit_all: false }); setF({ ...f, pos: "", pos_dept: "" }); toast.success("Cargo criado"); refresh(); };
  const del = async (kind, id) => { await api.delete(`/${kind}/${id}`); toast.success("Removido"); refresh(); };

  // Constrói uma trilha "efetiva" a partir do que o backend devolveu.
  const effectiveTrack = (p) => {
    if (p.track && p.track.length > 0) return p.track;
    return (p.required_course_ids || []).map((cid) => ({ course_id: cid, delay_days: 0, mandatory: true }));
  };

  const persistPos = async (p, patch) => {
    const payload = {
      name: p.name,
      department_id: p.department_id || null,
      required_course_ids: (patch.track || effectiveTrack(p)).map((t) => t.course_id), // legacy fallback
      track: patch.track || effectiveTrack(p),
      screen_permissions: patch.screen_permissions !== undefined ? patch.screen_permissions : (p.screen_permissions ?? null),
      can_edit_all: patch.can_edit_all !== undefined ? patch.can_edit_all : !!p.can_edit_all,
    };
    await api.patch(`/positions/${p.id}`, payload);
    refresh();
  };

  const toggleCourseInTrack = async (p, courseId) => {
    const track = effectiveTrack(p);
    const has = track.find((t) => t.course_id === courseId);
    const next = has ? track.filter((t) => t.course_id !== courseId) : [...track, { course_id: courseId, delay_days: 0, mandatory: true }];
    await persistPos(p, { track: next });
  };

  const updateTrackItem = async (p, courseId, patch) => {
    const track = effectiveTrack(p).map((t) => (t.course_id === courseId ? { ...t, ...patch } : t));
    await persistPos(p, { track });
  };

  const toggleScreen = async (p, key) => {
    const cur = p.screen_permissions == null ? null : [...p.screen_permissions];
    let next;
    if (cur === null) {
      // ativar customização: parte de "todas as telas listadas" menos a que o admin desmarcou
      const all = SCREEN_GROUPS.flatMap((g) => g.keys.map(([k]) => k));
      next = all.filter((k) => k !== key);
    } else if (cur.includes(key)) {
      next = cur.filter((k) => k !== key);
    } else {
      next = [...cur, key];
    }
    await persistPos(p, { screen_permissions: next });
  };

  const resetScreensToDefault = async (p) => {
    await persistPos(p, { screen_permissions: null });
    toast.success("Voltou ao padrão do perfil");
  };

  return (
    <div className="space-y-8" data-testid="admin-org-page">
      <header>
        <div className="eyebrow">Estrutura</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Filiais, Departamentos e Cargos</h1>
        <p className="text-slate-600 mt-2 text-sm">Defina a estrutura organizacional, vincule trilhas com liberação temporizada e configure permissões de tela por cargo.</p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="branches" data-testid="tab-branches">Filiais</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">Departamentos</TabsTrigger>
          <TabsTrigger value="positions" data-testid="tab-positions">Cargos & Trilhas</TabsTrigger>
        </TabsList>

        <TabsContent value="branches" className="mt-6 space-y-4">
          <Card><CardContent className="p-5 grid sm:grid-cols-3 gap-3 items-end">
            <div><Label>Nome</Label><Input value={f.branch} onChange={(e) => setF({ ...f, branch: e.target.value })} data-testid="branch-name" /></div>
            <div><Label>Localização</Label><Input value={f.branch_loc} onChange={(e) => setF({ ...f, branch_loc: e.target.value })} /></div>
            <Button onClick={addBranch} className="btn-navy" data-testid="branch-add"><Plus className="w-4 h-4 mr-2" />Adicionar filial</Button>
          </CardContent></Card>
          <ListCard items={branches} onDel={(id) => del("branches", id)} fields={[["name", "Filial"], ["location", "Localização"]]} />
        </TabsContent>

        <TabsContent value="departments" className="mt-6 space-y-4">
          <Card><CardContent className="p-5 grid sm:grid-cols-3 gap-3 items-end">
            <div><Label>Nome</Label><Input value={f.dept} onChange={(e) => setF({ ...f, dept: e.target.value })} data-testid="dept-name" /></div>
            <div><Label>Filial</Label>
              <select className="w-full border border-input rounded-md px-3 py-2 text-sm" value={f.dept_branch} onChange={(e) => setF({ ...f, dept_branch: e.target.value })}>
                <option value="">—</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <Button onClick={addDept} className="btn-navy" data-testid="dept-add"><Plus className="w-4 h-4 mr-2" />Adicionar departamento</Button>
          </CardContent></Card>
          <ListCard items={departments} onDel={(id) => del("departments", id)} fields={[["name", "Departamento"]]} />
        </TabsContent>

        <TabsContent value="positions" className="mt-6 space-y-4">
          <Card><CardContent className="p-5 grid sm:grid-cols-3 gap-3 items-end">
            <div><Label>Cargo</Label><Input value={f.pos} onChange={(e) => setF({ ...f, pos: e.target.value })} data-testid="pos-name" /></div>
            <div><Label>Departamento</Label>
              <select className="w-full border border-input rounded-md px-3 py-2 text-sm" value={f.pos_dept} onChange={(e) => setF({ ...f, pos_dept: e.target.value })}>
                <option value="">—</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <Button onClick={addPos} className="btn-navy" data-testid="pos-add"><Plus className="w-4 h-4 mr-2" />Adicionar cargo</Button>
          </CardContent></Card>

          <div className="space-y-4">
            {positions.map((p) => {
              const track = effectiveTrack(p);
              const isOpen = !!expanded[p.id];
              const inTrack = new Set(track.map((t) => t.course_id));
              const permsCustom = p.screen_permissions != null;
              return (
                <Card key={p.id} data-testid={`pos-card-${p.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <button className="flex items-center gap-2 text-left" onClick={() => setExpanded({ ...expanded, [p.id]: !isOpen })}>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                        <div>
                          <div className="font-display font-bold text-lg">{p.name}</div>
                          <div className="text-xs text-slate-500">
                            Departamento: {departments.find((d) => d.id === p.department_id)?.name || "—"} · Trilha: {track.length} curso{track.length !== 1 ? "s" : ""} · Permissões: {permsCustom ? `${p.screen_permissions.length} tela(s)` : "padrão do perfil"}
                          </div>
                        </div>
                      </button>
                      <Button size="sm" variant="ghost" onClick={() => del("positions", p.id)} data-testid={`pos-del-${p.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                    </div>

                    {isOpen && (
                      <div className="mt-5 space-y-6">
                        {/* Trilha */}
                        <div>
                          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-2">
                            <Clock className="w-3 h-3" />Trilha de aprendizagem
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {courses.length === 0 && <div className="text-sm text-slate-500">Cadastre cursos antes para vincular.</div>}
                            {courses.map((c) => (
                              <button
                                key={c.id}
                                onClick={() => toggleCourseInTrack(p, c.id)}
                                data-testid={`pos-${p.id}-course-${c.id}`}
                                className={"text-xs px-3 py-1.5 rounded-full border " + (inTrack.has(c.id) ? "bg-[var(--unigr-navy)] text-white border-[var(--unigr-navy)]" : "border-[var(--unigr-border)] text-slate-600 hover:bg-slate-50")}
                              >
                                {c.title}
                              </button>
                            ))}
                          </div>
                          {track.length > 0 && (
                            <div className="border border-[var(--unigr-border)] rounded-md overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                                  <tr>
                                    <th className="text-left px-3 py-2">Curso</th>
                                    <th className="text-left px-3 py-2 w-40">Liberação (dias após admissão)</th>
                                    <th className="text-left px-3 py-2 w-32">Obrigatório?</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {track.map((t) => {
                                    const c = courses.find((cc) => cc.id === t.course_id);
                                    return (
                                      <tr key={t.course_id} className="border-t border-[var(--unigr-border)]">
                                        <td className="px-3 py-2">{c?.title || <span className="text-slate-400 italic">curso removido</span>}</td>
                                        <td className="px-3 py-2">
                                          <Input
                                            type="number"
                                            min={0}
                                            value={t.delay_days}
                                            onChange={(e) => updateTrackItem(p, t.course_id, { delay_days: parseInt(e.target.value || "0", 10) })}
                                            data-testid={`track-delay-${p.id}-${t.course_id}`}
                                            className="h-8 text-sm"
                                          />
                                        </td>
                                        <td className="px-3 py-2">
                                          <label className="inline-flex items-center gap-2 text-xs">
                                            <input
                                              type="checkbox"
                                              checked={t.mandatory !== false}
                                              onChange={(e) => updateTrackItem(p, t.course_id, { mandatory: e.target.checked })}
                                              data-testid={`track-mandatory-${p.id}-${t.course_id}`}
                                            />
                                            {t.mandatory !== false ? "Obrigatório" : "Recomendado"}
                                          </label>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* Permissões de tela */}
                        <div>
                          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-2 justify-between">
                            <span className="flex items-center gap-2"><ShieldCheck className="w-3 h-3" />Permissões de tela por cargo</span>
                            {permsCustom && (
                              <Button size="sm" variant="ghost" onClick={() => resetScreensToDefault(p)} className="text-xs h-6" data-testid={`perms-reset-${p.id}`}>
                                Voltar ao padrão
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mb-3">
                            {permsCustom
                              ? "Este cargo tem permissões customizadas. Elas RESTRINGEM o que o perfil padrão permitiria (nunca ampliam)."
                              : "Este cargo usa o padrão do perfil do usuário (Master, Admin, Gestor ou Colaborador). Marque qualquer tela para começar a customizar."}
                          </p>
                          <div className="grid md:grid-cols-3 gap-4">
                            {SCREEN_GROUPS.map((grp) => (
                              <div key={grp.label}>
                                <div className="text-[10px] uppercase text-slate-400 mb-1">{grp.label}</div>
                                <div className="space-y-1">
                                  {grp.keys.map(([k, label]) => {
                                    const active = permsCustom ? (p.screen_permissions || []).includes(k) : true;
                                    return (
                                      <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={active}
                                          onChange={() => toggleScreen(p, k)}
                                          data-testid={`perm-${p.id}-${k}`}
                                        />
                                        {label}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Permissão total de edição (Master-like) */}
                        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-4">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!p.can_edit_all}
                              onChange={(e) => persistPos(p, { can_edit_all: e.target.checked })}
                              data-testid={`pos-can-edit-all-${p.id}`}
                              className="mt-1"
                            />
                            <div>
                              <div className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4" />
                                Permissão total de edição (super-usuário)
                              </div>
                              <p className="text-xs text-amber-800/80 mt-1">
                                Usuários com este cargo poderão criar, editar e excluir qualquer registro da plataforma
                                (cursos, colaboradores, cargos, filiais, biblioteca, configurações), equiparado a um administrador de empresa.
                                Use com cautela — recomendado apenas para cargos de gestão de RH/T&D.
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ListCard({ items, onDel, fields }) {
  return (
    <Card><CardContent className="p-0">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>{fields.map((f) => <th key={f[0]} className="px-4 py-3">{f[1]}</th>)}<th className="px-4 py-3 text-right">Ações</th></tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={fields.length + 1} className="px-4 py-8 text-center text-slate-500">Sem itens.</td></tr>}
          {items.map((it) => (
            <tr key={it.id} className="border-t border-[var(--unigr-border)]">
              {fields.map((f) => <td key={f[0]} className="px-4 py-3">{it[f[0]] || "—"}</td>)}
              <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => onDel(it.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}
